import React from "react";
import { Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { foliosApi } from "api/endpoints";
import { useShallow } from "zustand/react/shallow";
import { useEmissionAmbienteStore } from "stores/emissionAmbienteStore";
import { SII_SOLICITA_FOLIOS_URL } from "utils/cafXmlPreview";
import CafFolioAvailabilityHeader from "views/admin/folios/components/CafFolioAvailabilityHeader";
import CafStockTable from "views/admin/tables/components/CafStockTable";
import FolioSequencesTable from "views/admin/tables/components/FolioSequencesTable";

/**
 * Pestañas con Tailwind: Chakra `variant="enclosed"` no sigue el tema por clase `dark` en `body`.
 */
const CAF_TAB_BTN_CLASS =
  "rounded-lg px-4 py-2.5 text-sm font-semibold outline-none transition-colors " +
  "text-gray-600 hover:bg-white/95 hover:text-gray-900 " +
  "dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white " +
  "focus-visible:ring-2 focus-visible:ring-brand-400/45 focus-visible:ring-offset-2 " +
  "focus-visible:ring-offset-transparent dark:focus-visible:ring-brand-300/45 " +
  "aria-[selected=true]:bg-white aria-[selected=true]:text-brand-600 aria-[selected=true]:shadow-sm " +
  "dark:aria-[selected=true]:bg-navy-800 dark:aria-[selected=true]:text-brand-300 " +
  "dark:aria-[selected=true]:shadow-[inset_0_0_0_1px_rgba(200,165,206,0.35)]";

const CAF_TAB_LIST_CLASS =
  "flex w-full flex-wrap gap-2 rounded-xl border border-gray-200 bg-gray-50/95 p-1.5 shadow-sm " +
  "dark:border-white/12 dark:bg-navy-900/75 dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)]";

/**
 * CAF: CAF stock + secuencias vigentes (folio disponible). Sin reservas (uso interno / API).
 * URL: /admin/caf
 */
export default function CafHubPage() {
  const emission = useEmissionAmbienteStore(
    useShallow((s) => ({
      kind: s.kind,
      ambiente: s.ambiente,
      profileUuid: s.profileUuid,
    })),
  );
  const selectedAmbiente =
    emission.kind === "ready" && emission.ambiente ? emission.ambiente : "";
  const selectedProfileUuid =
    emission.kind === "ready" && emission.profileUuid ? emission.profileUuid : "";
  const [folioSeqItems, setFolioSeqItems] = React.useState([]);
  const [folioSeqLoading, setFolioSeqLoading] = React.useState(true);
  const [folioSeqError, setFolioSeqError] = React.useState("");

  const loadFolioSequences = React.useCallback(async () => {
    if (!selectedAmbiente) {
      setFolioSeqLoading(false);
      setFolioSeqItems([]);
      return;
    }
    setFolioSeqLoading(true);
    setFolioSeqError("");
    try {
      const qs = new URLSearchParams();
      qs.set("ambiente", selectedAmbiente);
      if (selectedProfileUuid) qs.set("profileUuid", String(selectedProfileUuid).trim());
      const { res, data } = await foliosApi.listSequences(`?${qs.toString()}`);
      if (!res.ok || !data?.ok) {
        setFolioSeqError(
          data?.error || data?.message || `Error ${res.status}`
        );
        setFolioSeqItems([]);
        return;
      }
      setFolioSeqItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setFolioSeqError(
        e instanceof Error ? e.message : "Error al cargar secuencias."
      );
      setFolioSeqItems([]);
    } finally {
      setFolioSeqLoading(false);
    }
  }, [selectedAmbiente, selectedProfileUuid]);

  React.useEffect(() => {
    loadFolioSequences();
  }, [loadFolioSequences]);

  const headerProps = {
    items: folioSeqItems,
    loading: folioSeqLoading,
    error: folioSeqError,
    onRefresh: loadFolioSequences,
  };

  return (
    <div className="mt-3">
      <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
        Para obtener folios y el archivo CAF (XML) desde el SII (certificación),
        ingresá a{" "}
        <a
          href={SII_SOLICITA_FOLIOS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-brand-600 underline decoration-brand-600/40 underline-offset-2 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
        >
          https://maullin.sii.cl/cvc_cgi/dte/of_solicita_folios
        </a>
        , solicitá folios y descargá el CAF; luego podés subirlo con{" "}
        <strong className="text-navy-700 dark:text-white">Subir CAF</strong> en el
        resumen de arriba.
      </p>
      <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
        <strong className="text-navy-700 dark:text-white">Resumen de folios</strong> arriba
        de cada pestaña; <strong>CAF stock</strong> son los XML cargados;{" "}
        <strong>Secuencias vigentes</strong> son las líneas con estado «disponible» para
        emitir. APIs:{" "}
        <code className="rounded bg-lightPrimary px-1 text-[11px] dark:bg-navy-800">
          /api/v1/caf-stock
        </code>
        ,{" "}
        <code className="rounded bg-lightPrimary px-1 text-[11px] dark:bg-navy-800">
          /api/v1/folio-sequences
        </code>
        .
      </p>

      <Tabs variant="unstyled" isLazy lazyBehavior="keepMounted">
        <TabList className={CAF_TAB_LIST_CLASS}>
          <Tab className={CAF_TAB_BTN_CLASS}>CAF stock</Tab>
          <Tab className={CAF_TAB_BTN_CLASS}>Secuencias vigentes</Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0} pt={6}>
            <CafFolioAvailabilityHeader {...headerProps} />
            <CafStockTable />
          </TabPanel>
          <TabPanel px={0} pt={6}>
            <CafFolioAvailabilityHeader {...headerProps} />
            <FolioSequencesTable />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  );
}
